import type { Instance, Props } from "tippy.js"
import { ReactRenderer } from "@tiptap/react"
import tippy from "tippy.js"

import type { MacroVariable } from "@productlane/lib/src/macro-variables"

import type {
	MacroVariableCommandProps,
	MacroVariableListProps,
	MacroVariableOnKeyDownHandler,
	MacroVariableOnKeyDownHandlerSetter,
} from "./types"
import type { SuggestionOptions } from "@tiptap/suggestion"
import MacroVariableList from "./macro-variable-list"

// The macro editor lives in a non-modal Radix Dialog, so the popup mounts next
// to the editor instead of document.body or the dialog swallows its clicks.
export const macroVariableSuggestionRenderer: NonNullable<
	SuggestionOptions<MacroVariable, MacroVariableCommandProps>["render"]
> = () => {
	let component: ReactRenderer<unknown, MacroVariableListProps> | null = null
	let popup: Instance<Props>[] | null = null
	let onKeyDownHandler: MacroVariableOnKeyDownHandler = () => false
	const setOnKeyDownHandler: MacroVariableOnKeyDownHandlerSetter = (
		handler,
	) => {
		onKeyDownHandler = handler
	}

	let dismissed = false

	const syncVisibility = (items: MacroVariable[]) => {
		if (dismissed || items.length === 0) {
			popup?.[0]?.hide()
			return
		}
		popup?.[0]?.show()
	}

	return {
		onStart: (props) => {
			component = new ReactRenderer(MacroVariableList, {
				props: { ...props, setOnKeyDownHandler },
				editor: props.editor,
			})

			if (!props.clientRect) return

			// @ts-expect-error tippy types are wrong
			popup = tippy("body", {
				getReferenceClientRect: props.clientRect,
				appendTo: () => props.editor.options.element,
				content: component.element,
				showOnCreate: true,
				interactive: true,
				trigger: "manual",
				placement: "bottom-start",
				zIndex: 9999,
			})

			syncVisibility(props.items)
		},

		onUpdate(props) {
			component?.updateProps({ ...props, setOnKeyDownHandler })

			const { clientRect } = props
			if (clientRect) {
				popup?.[0]?.setProps({
					getReferenceClientRect: () => clientRect() ?? new DOMRect(),
				})
			}

			syncVisibility(props.items)
		},

		onKeyDown(props) {
			if (props.event.key === "Escape") {
				dismissed = true
				popup?.[0]?.hide()
				return true
			}

			return onKeyDownHandler(props)
		},

		onExit() {
			popup?.[0]?.destroy()
			component?.destroy()
			popup = null
			component = null
			dismissed = false
		},
	}
}
